import React, { useState, useEffect } from 'react';
import { Box, Stack, Typography, Link, IconButton, Modal, TextField, Button } from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import PersonIcon from '@mui/icons-material/Person';
import YouTubeIcon from '@mui/icons-material/YouTube';
import EditIcon from '@mui/icons-material/Edit';
import { addLinks, getLinksByUserId, updateLinks, getUserById } from '../utils/api';
import PhoneIcon from '@mui/icons-material/Phone';

const Footer = ({ userId }) => {
  const [links, setLinks] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    instagramLink: '',
    facebookLink: '',
    websiteLink: '',
    youtubeLink: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userData = await getUserById(userId);
        setCurrentUser(userData);

        const linksUserId = userData.role === 'trainee' && userData.trainer_id ? userData.trainer_id : userId;

        try {
          const linksData = await getLinksByUserId(linksUserId);
          setLinks(linksData);

          if (userData.role === 'trainer' && userData._id === userId) {
            setFormData({
              instagramLink: linksData.instagramLink || '',
              facebookLink: linksData.facebookLink || '',
              websiteLink: linksData.websiteLink || '',
              youtubeLink: linksData.youtubeLink || '',
            });
          }
        } catch (error) {
          console.error('Failed to fetch links:', error);
          if (error.response?.status === 404) {
            setLinks({});
          }
        }
      } catch (error) {
        console.error('Failed to fetch current user:', error);
      }
    };

    if (userId) {
      fetchData();
    }
  }, [userId]);

  const isTrainerViewingOwnFooter =
    currentUser && currentUser.role === 'trainer' && currentUser._id === userId;

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  // Normalize URL by adding https:// if missing
  const normalizeUrl = (url) => {
    if (!url) return '';
    if (!/^https?:\/\//i.test(url)) {
      return `https://${url}`;
    }
    return url;
  };

  // Handle form input changes with phone number formatting and URL normalization
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveLinks = async () => {
    try {
      const dataToSave = {
        instagramLink: normalizeUrl(formData.instagramLink),
        facebookLink: normalizeUrl(formData.facebookLink),
        websiteLink: normalizeUrl(formData.websiteLink),
        youtubeLink: normalizeUrl(formData.youtubeLink),
      };
      const updatedLinks = await updateLinks(userId, dataToSave);
      setLinks(updatedLinks.links);
      handleCloseModal();
      window.location.reload();
    } catch (error) {
      console.error('Failed to update links:', error);
    }
  };

  return (
    <Box mt="80px" bgcolor="#FFF3F4">
      <Stack gap="20px" sx={{ alignItems: 'center' }} flexWrap="wrap" px="40px" pt="24px">
        <Stack direction="row" gap="20px" justifyContent="center">
          {links?.instagramLink && (
            <IconButton
              component="a"
              href={normalizeUrl(links.instagramLink)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <InstagramIcon />
            </IconButton>
          )}
          {links?.facebookLink && (
            <IconButton
              component="a"
              href={normalizeUrl(links.facebookLink)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
            >
              <FacebookIcon />
            </IconButton>
          )}
          {links?.websiteLink && (
            <IconButton
              component="a"
              href={normalizeUrl(links.websiteLink)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Website"
            >
              <PersonIcon />
            </IconButton>
          )}
          {links?.youtubeLink && (
            <IconButton
              component="a"
              href={normalizeUrl(links.youtubeLink)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
            >
              <YouTubeIcon />
            </IconButton>
          )}
          {currentUser?.phone && (
            <IconButton
              component="a"
              href={`tel:${currentUser.phone}`}
              aria-label="Phone"
            >
              <PhoneIcon />
            </IconButton>
          )}

          {isTrainerViewingOwnFooter && (
            <IconButton onClick={handleOpenModal} aria-label="עדכון רשתות חברתיות">
              <EditIcon />
            </IconButton>
          )}
        </Stack>

        <Modal open={openModal} onClose={handleCloseModal}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: 'background.paper',
              justifyContent: 'right',
              direction: 'rtl',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
            }}
          >
            <Typography color="#B5A78A" fontFamily="GveretLevin" variant="h6" mb={2}>
              עדכון רשתות חברתיות
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="ערוץ אינסטגרם"
                name="instagramLink"
                value={formData.instagramLink}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                label="ערוץ פייסבוק"
                name="facebookLink"
                value={formData.facebookLink}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                label="אתר של העסק"
                name="websiteLink"
                value={formData.websiteLink}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                label="ערוץ יוטיוב"
                name="youtubeLink"
                value={formData.youtubeLink}
                onChange={handleInputChange}
                fullWidth
              />

              <Button
                sx={{
                  backgroundColor: "#C4A484",
                  "&:hover": { backgroundColor: "#C4A484" },
                }}
                variant="contained"
                onClick={handleSaveLinks}
              >
                שמירה
              </Button>
            </Stack>
          </Box>
        </Modal>

        <Typography variant="body2" textAlign="center" sx={{ fontSize: { lg: '12px', xs: '10px' } }}>
          <Link href="mailto:onryxx@gmail.com" color="inherit" underline="hover">
            Report Bugs
          </Link>
          {" | "}
          <Link href="mailto:onryxx@gmail.com" color="inherit" underline="hover">
            Contact Us
          </Link>
        </Typography>
      </Stack>
      <Typography variant="body2" mt="20px" textAlign="center" pb="20px" sx={{ fontSize: { lg: '10px', xs: '8px' } }}>
        © 2025 ONRYX - FIT. All Rights Reserved.
      </Typography>
    </Box>
  );
};

export default Footer;